import {
    AfterViewInit,
    Component,
    ComponentRef,
    computed,
    effect,
    Injector,
    OnDestroy,
    signal,
    StaticProvider,
    Type,
    ViewChild,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import { IconKey } from '@common/classes/icons';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import _ from 'lodash';
import { AppControlComponent, FormProperty } from '../app-control/app-control.component';

export interface DialogFormOptions {
    title: string;
    titleIcons?: IconKey[];
    message?: string;
    model?: any;
    cancelText?: string;
    cancelIcon?: IconKey;
    cancelAction?: Function;
    confirmText?: string;
    confirmIcon?: IconKey;
    confirmMessage?: string;
    properties?: FormProperty[];
    canEdit?: (model: any) => boolean;
    canConfirm?: (model: any) => boolean;
    contentComponent?: Type<any>;
    contentComponentDataProviders?: StaticProvider[];
    onContentComponentCreated?: (component: any) => void;
}

@Component({
    selector: 'app-dialog-form',
    templateUrl: './dialog-form.component.html'
})
export class DialogFormComponent extends DialogContentBase implements OnDestroy, AfterViewInit {
    @ViewChild('template', { static: false, read: ViewContainerRef }) templateRef: ViewContainerRef;

    private contentComponentRef: ComponentRef<any>;
    private injector: Injector;

    // Convert model to signal for reactivity
    private modelSignal = signal<any>({});

    // Computed signal for reactive custom filters
    customFilters = computed(() => {
        const currentModel = this.modelSignal();
        const filters: { [key: string]: string } = {};

        this.options.properties?.forEach((prop) => {
            if (prop.customFilter) {
                filters[prop.name] =
                    typeof prop.customFilter === 'function' ? prop.customFilter(currentModel) : prop.customFilter;
            }
        });

        return filters;
    });

    // Getter for model that returns the signal value
    get model(): any {
        return this.modelSignal();
    }

    // Setter for model that updates the signal
    set model(value: any) {
        this.modelSignal.set(value);
    }

    options: DialogFormOptions = {
        title: 'Form',
        cancelText: 'Cancel',
        confirmText: 'Confirm'
    };
    confirmAction: Function;
    canConfirmPredicate: (model: any) => boolean = () => true;
    canEditPredicate: (model: any) => boolean = () => true;
    cancelAction: Function;

    @ViewChildren(AppControlComponent) appControls: AppControlComponent[];

    constructor(
        public override dialog: DialogRef,
        private toastrNotificationService: ToastrNotificationService
    ) {
        super(dialog);

        // Effect to trigger codelist refresh when custom filters change
        effect(() => {
            const filters = this.customFilters();
            // Trigger re-query for controls with dynamic custom filters
            this.refreshCodelistControls(filters);
        });
    }

    public initialize(options: DialogFormOptions, injector: Injector): void {
        for (const [key, value] of Object.entries(options)) {
            if (value != null) this.options[key] = value;
        }

        if (this.options.cancelAction) this.cancelAction = this.options.cancelAction;
        const initialModel = this.options.model || {};
        this.options.properties?.forEach((prop) => (initialModel[prop.name] = prop.initialValue));
        this.modelSignal.set(initialModel);
        this.injector = injector;
        this.canConfirmPredicate = this.options.canConfirm || this.canConfirm.bind(this);
        this.canEditPredicate = this.options.canEdit || this.canEdit.bind(this);
    }

    // Method to update model property and trigger reactivity
    updateModelProperty(propertyName: string, value: any): void {
        if (value === undefined) value = null;
        const currentModel = { ...this.modelSignal() };
        currentModel[propertyName] = value;
        this.modelSignal.set(currentModel);

        // Find the property and execute onChange if defined
        const property = this.options.properties?.find((p) => p.name === propertyName);
        if (property?.onChange) {
            property.onChange(value, currentModel, this);
        }
    }

    // Method to refresh codelist controls when filters change
    private refreshCodelistControls(filters: { [key: string]: string }): void {
        if (!this.appControls) return;

        // Get properties that have dynamic custom filters
        const dynamicFilterProperties =
            this.options.properties?.filter((prop) => typeof prop.customFilter === 'function') || [];

        this.appControls.forEach((control) => {
            // Find the corresponding property for this control
            const property = dynamicFilterProperties.find((prop) => {
                // Get the property name from the control's ngModel
                const controlPropertyName = this.getControlPropertyName(control);
                return controlPropertyName === prop.name;
            });

            if (property && control.type === 'codelist') {
                const newFilter = filters[property.name];
                if (newFilter !== control.customFilter) {
                    // Update the customFilter and trigger re-query
                    control.customFilter = newFilter;
                    control.queryCodelist();
                }
            }
        });
    }

    // Helper method to get property name from a control
    private getControlPropertyName(control: AppControlComponent): string | null {
        // Try to extract property name from the control's context
        // This is a bit tricky since we need to match the control to its property
        try {
            // Look for the property based on the control's position or other identifying features
            const controlIndex = Array.from(this.appControls).indexOf(control);
            const property = this.options.properties?.[controlIndex];
            return property?.name || null;
        } catch {
            return null;
        }
    }

    confirm(): void {
        if (this.appControls.some((x) => !!x.error)) {
            this.toastrNotificationService.show({ type: 'error', message: 'Some required fields are empty' });
            return;
        }
        this.dialog.close(this.modelSignal());
    }

    canConfirm(model: any): boolean {
        return true;
    }

    canEdit(model: any): boolean {
        return true;
    }

    close(): void {
        this.dialog.close(null);
    }

    clear(): void {
        if (this.cancelAction) this.cancelAction();
        this.modelSignal.set({});
    }

    private loadComponent(component, dataProviders: StaticProvider[]): void {
        dataProviders = (dataProviders || []).concat([{ provide: DialogRef, useValue: this.dialog }]);
        const injector = Injector.create({ providers: dataProviders || [], parent: this.injector });
        this.contentComponentRef = this.templateRef.createComponent(component, { injector });
        if (!this.contentComponentRef.instance.model) {
            this.contentComponentRef.instance.model = this.modelSignal();
        } else {
            this.modelSignal.set(this.contentComponentRef.instance.model);
        }

        if (_.isFunction(this.contentComponentRef.instance.confirm)) {
            this.confirmAction = this.contentComponentRef.instance.confirm.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.contentComponentRef.instance.canConfirm)) {
            this.canConfirmPredicate = this.contentComponentRef.instance.canConfirm.bind(
                this.contentComponentRef.instance
            );
        }

        if (_.isFunction(this.contentComponentRef.instance.canEdit)) {
            this.canEditPredicate = this.contentComponentRef.instance.canEdit.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.contentComponentRef.instance.cancel)) {
            this.cancelAction = this.contentComponentRef.instance.cancel.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.options.onContentComponentCreated)) {
            this.options.onContentComponentCreated(this.contentComponentRef.instance);
        }

        this.contentComponentRef.changeDetectorRef.detectChanges();
    }

    override ngAfterViewInit(): void {
        if (this.options.contentComponent) {
            this.loadComponent(this.options.contentComponent, this.options.contentComponentDataProviders);
        }

        this.confirmAction = this.confirmAction || this.confirm.bind(this);
        this.cancelAction = this.cancelAction || this.close.bind(this);
    }

    ngOnDestroy(): void {
        if (this.contentComponentRef) {
            this.contentComponentRef.destroy();
        }
    }

    getName(property: FormProperty): string {
        return property.name;
    }

    getCustomFilter(property: FormProperty): string {
        return typeof property.customFilter === 'function'
            ? property.customFilter(this.modelSignal())
            : property.customFilter;
    }

    isRequired(property: FormProperty): boolean {
        return typeof property.required === 'function'
            ? property.required(this.modelSignal())
            : (property.required ?? false);
    }
}
